import {CardElement} from '@stripe/react-stripe-js';
import {Stripe, StripeElements} from '@stripe/stripe-js/types/stripe-js';
import {Button} from '@ui/atoms/button';
import {Helper} from '@ui/atoms/helper';
import {CancelIcon} from '@ui/atoms/icons';
import {TextField} from '@ui/molecules/field/textfield';
import {SelectField} from '@ui/molecules/select/selectfield';
import {City, Country} from 'country-state-city';
import {ICity, ICountry} from 'country-state-city/dist/lib/interface';
import {useEffect, useMemo, useState} from 'react';
import {Controller} from 'react-hook-form';
import {useTheme} from 'styled-components';
import {lockElementScroll} from '@utils/scroll-lock';
import {useBillingHook} from '../../../admin-billing.hooks';
import {getBillingCardOptions} from '../../billing-not-active/components/billng-cycle';
import {
  SecurePaymentNote,
  StripeCardField,
  UpdateCardAddressSummary,
  UpdateCardBody,
  UpdateCardCloseButton,
  UpdateCardField,
  UpdateCardFieldLabel,
  UpdateCardFooter,
  UpdateCardHeader,
  UpdateCardIntro,
  UpdateCardOverlay,
  UpdateCardSection,
  UpdateCardSectionActions,
  UpdateCardSectionHeader,
  UpdateCardSectionSubtitle,
  UpdateCardSectionTitle,
  UpdateCardSidecar,
  UpdateCardTitle,
} from './update-card.styles';

interface IUpdate {
  open: boolean;
  onClose: () => void;
  stripe: Stripe | null;
  elements: StripeElements | null;
}

export const UpdateCard = ({open, onClose, stripe, elements}: IUpdate) => {
  const theme = useTheme();
  const {
    errorsCard,
    updateAddressDetails,
    addressDetails,
    controlCard,
    handleFormValueChangeCard,
    stripeError,
    updatingCard,
    setStripeError,
    setSelectedCountry,
    selectedCountry,
    addressSaved,
    setSaveAddress,
    cardComplete,
    setCardComplete,
    handleUpdatePaymentMethod,
  } = useBillingHook();

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [cardholderName, setCardholderName] = useState('');

  const disableSavedButton =
    !addressDetails.country || !addressDetails.line1 || !addressDetails.city;
  const disableUpdateButton =
    !stripe ||
    !elements ||
    !addressSaved ||
    !cardComplete ||
    !cardholderName.trim() ||
    updatingCard;
  const cardOptions = useMemo(() => getBillingCardOptions(theme.colors), [
    theme.colors,
  ]);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  useEffect(() => {
    if (selectedCountry.length > 0) {
      setCities(City.getCitiesOfCountry(selectedCountry) || []);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (!open) return;

    const releaseScrollLock = lockElementScroll(document.body);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !updatingCard) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      releaseScrollLock();
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose, open, updatingCard]);

  if (!open) return null;

  return (
    <>
      <UpdateCardOverlay
        aria-hidden="true"
        onMouseDown={() => !updatingCard && onClose()}
      />
      <UpdateCardSidecar
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-payment-method-title"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdatePaymentMethod(
            stripe,
            elements,
            onClose,
            cardholderName.trim(),
          );
        }}>
        <UpdateCardHeader>
          <UpdateCardTitle id="update-payment-method-title">
            Update payment method
          </UpdateCardTitle>
          <UpdateCardCloseButton
            type="button"
            disabled={updatingCard}
            onClick={onClose}>
            <CancelIcon />
            Close
          </UpdateCardCloseButton>
        </UpdateCardHeader>

        <UpdateCardBody>
          <UpdateCardIntro>
            Update the billing address and card used for subscription renewals
            and seat changes.
          </UpdateCardIntro>

          <UpdateCardSection>
            <UpdateCardSectionHeader>
              <div>
                <UpdateCardSectionTitle>Billing address</UpdateCardSectionTitle>
                <UpdateCardSectionSubtitle>
                  Used to verify your payment method.
                </UpdateCardSectionSubtitle>
              </div>
              {addressSaved && (
                <Button
                  type="button"
                  kind="small"
                  onClick={() => setSaveAddress(false)}>
                  Edit
                </Button>
              )}
            </UpdateCardSectionHeader>

            {!addressSaved ? (
              <>
                <Controller
                  control={controlCard}
                  name="billing_details.address.line1"
                  defaultValue={addressDetails.line1}
                  render={({onBlur}) => (
                    <UpdateCardField>
                      <TextField
                        label="Address"
                        placeholder="Street address"
                        type="text"
                        value={addressDetails.line1}
                        onBlur={onBlur}
                        onChange={(event) => {
                          updateAddressDetails('line1', event.target.value);
                          handleFormValueChangeCard(
                            event.target.value,
                            'billing_details.address.line1',
                          );
                        }}
                        helper={
                          errorsCard.billing_details?.address?.line1?.message ||
                          ''
                        }
                        state={
                          errorsCard.billing_details?.address?.line1
                            ? 'error'
                            : 'default'
                        }
                      />
                    </UpdateCardField>
                  )}
                />

                <Controller
                  control={controlCard}
                  name="billing_details.address.country"
                  render={({onBlur}) => (
                    <UpdateCardField>
                      <UpdateCardFieldLabel>Country</UpdateCardFieldLabel>
                      <SelectField
                        onBlur={onBlur}
                        borderRadius="8px"
                        name="billing_details.address.country"
                        defaultValue={addressDetails.country}
                        helper={
                          errorsCard.billing_details?.address?.country
                            ?.message || ''
                        }
                        state={
                          errorsCard.billing_details?.address?.country
                            ? 'error'
                            : 'default'
                        }
                        onChange={(data: {value: string; isoCode: string}) => {
                          if (data?.value) {
                            handleFormValueChangeCard(
                              data.value,
                              'billing_details.address.country',
                            );
                            setSelectedCountry(data.isoCode);
                            updateAddressDetails('country', data.value);
                          } else {
                            handleFormValueChangeCard(
                              null,
                              'billing_details.address.country',
                            );
                            updateAddressDetails('country', null);
                          }
                        }}
                        placeholder="Select or search for a country"
                        options={countries.map((country) => ({
                          value: country.isoCode,
                          label: country.name,
                          isoCode: country.isoCode,
                        }))}
                      />
                    </UpdateCardField>
                  )}
                />

                <Controller
                  control={controlCard}
                  name="billing_details.address.city"
                  render={({onBlur, value}) => (
                    <UpdateCardField>
                      <UpdateCardFieldLabel>City</UpdateCardFieldLabel>
                      <SelectField
                        onBlur={onBlur}
                        borderRadius="8px"
                        name="billing_details.address.city"
                        value={value}
                        defaultValue={addressDetails.city}
                        helper={
                          errorsCard.billing_details?.address?.city?.message ||
                          ''
                        }
                        state={
                          errorsCard.billing_details?.address?.city
                            ? 'error'
                            : 'default'
                        }
                        onChange={(data: {value: string}) => {
                          handleFormValueChangeCard(
                            data?.value,
                            'billing_details.address.city',
                          );
                          updateAddressDetails('city', data?.value);
                        }}
                        placeholder="Select city"
                        options={cities.map((city) => ({
                          label: city.name,
                          value: city.name,
                        }))}
                      />
                    </UpdateCardField>
                  )}
                />

                <UpdateCardSectionActions>
                  <Button
                    type="button"
                    disabled={disableSavedButton}
                    style={{padding: '8px 16px'}}
                    onClick={() => setSaveAddress(true)}>
                    Save address
                  </Button>
                </UpdateCardSectionActions>
              </>
            ) : (
              <UpdateCardAddressSummary>
                {addressDetails.line1}, {addressDetails.city},{' '}
                {addressDetails.country}
              </UpdateCardAddressSummary>
            )}
          </UpdateCardSection>

          <UpdateCardSection>
            <UpdateCardSectionTitle>Card details</UpdateCardSectionTitle>
            <UpdateCardSectionSubtitle>
              This card will become your workspace’s primary payment method.
            </UpdateCardSectionSubtitle>
            <UpdateCardField>
              <TextField
                label="Name on card"
                placeholder="Enter the cardholder name"
                type="text"
                autoComplete="cc-name"
                value={cardholderName}
                onChange={(event) => setCardholderName(event.target.value)}
              />
            </UpdateCardField>
            <UpdateCardField>
              <UpdateCardFieldLabel>Card information</UpdateCardFieldLabel>
              <StripeCardField>
                <CardElement
                  options={cardOptions}
                  onChange={(event) => {
                    setStripeError(event.error?.message);
                    setCardComplete(event.complete);
                  }}
                />
              </StripeCardField>
              <Helper
                aria-label="helper"
                state={stripeError ? 'error' : 'default'}
                children={stripeError || ''}
              />
              <SecurePaymentNote>
                Your card information is securely processed by Stripe.
              </SecurePaymentNote>
            </UpdateCardField>
          </UpdateCardSection>
        </UpdateCardBody>

        <UpdateCardFooter>
          <Button
            type="submit"
            disabled={disableUpdateButton}
            isLoading={updatingCard}>
            Update card
          </Button>
        </UpdateCardFooter>
      </UpdateCardSidecar>
    </>
  );
};
